/**
 * Default values for Scene Editor elements
 * These provide sensible defaults when creating new elements
 */

import type {
  Position,
  Transform,
  Background,
  Sprite,
  SpriteState,
  Dialog,
  Sound,
  Scene,
  Story,
  PlaybackSettings,
  Transition,
  Animation,
  ResponsiveLayout,
} from '../../../Shared/src/@types';
import { v4 as uuid } from 'uuid';
import { DEFAULT_PLAYBACK_SETTINGS } from '../../../Shared/src/utils/sceneContentDefaults';
import { SOUND_TYPE_DEFAULTS } from '../../../Shared/src/utils/sceneTiming';

// =============================================================================
// POSITIONING DEFAULTS
// =============================================================================

export const defaultPosition: Position = {
  anchor: 'center',
  x: 50,
  y: 50,
  unit: '%',
};

export const defaultTransform: Transform = {
  scale: 1,
  rotation: 0,
  flipX: false,
  flipY: false,
};

/**
 * Create default responsive layout with position set for current orientation.
 * Position is centered at 50%, 50%.
 */
export const createDefaultLayout = (orientation: 'portrait' | 'landscape' | 'desktop' = 'portrait'): ResponsiveLayout => ({
  basis: orientation,
  [orientation]: {
    position: { ...defaultPosition },
  },
});

// =============================================================================
// BACKGROUND DEFAULTS
// =============================================================================

export const createDefaultBackground = (name?: string): Background => ({
  id: uuid(),
  name: name || 'Background',
  color: '#1a1a2e',
});

// =============================================================================
// SPRITE DEFAULTS
// =============================================================================

export const createDefaultSpriteState = (name: string, mediaId: number): SpriteState => ({
  name,
  mediaId,
  animation: {
    type: 'none',
    duration: 1,
    repeat: 0,
  },
});

export const createDefaultSprite = (name?: string, mediaId?: number, orientation: 'portrait' | 'landscape' | 'desktop' = 'portrait'): Sprite => {
  const defaultState = createDefaultSpriteState('idle', mediaId || 0);
  
  return {
    id: uuid(),
    name: name || 'New Sprite',
    layout: createDefaultLayout(orientation),
    transform: { ...defaultTransform },
    zIndex: 1,
    states: [defaultState],
    defaultState: 'idle',
  };
};

// =============================================================================
// DIALOG DEFAULTS
// =============================================================================

export const createDefaultDialogLine = (text?: string): { id: string; text?: string; audioId?: number; duration?: number } => ({
  id: uuid(),
  text: text || '',
  audioId: undefined,
  duration: 3, // Default 3 seconds
});

export const createDefaultDialog = (name?: string): Dialog => ({
  id: uuid(),
  name: name || 'New Dialog',
  position: 'bottom',
  lines: [createDefaultDialogLine('Enter text...')],
});

// =============================================================================
// SOUND DEFAULTS
// =============================================================================

export const createDefaultSound = (type: 'bgm' | 'sfx' | 'voice', mediaId: number): Sound => ({
  id: uuid(),
  name: type === 'bgm' ? 'Background Music' : type === 'sfx' ? 'Sound Effect' : 'Voice',
  mediaId,
  type,
  // Volume, loop and fades follow the type (spec 007, one table in Shared)
  ...SOUND_TYPE_DEFAULTS[type],
  pan: 0,
});

// =============================================================================
// ANIMATION DEFAULTS
// =============================================================================

export const defaultEnterAnimation: Animation = {
  type: 'fade',
  duration: 500,
  ease: 'power2.out',
};

export const defaultExitAnimation: Animation = {
  type: 'fade',
  duration: 300,
  ease: 'power2.in',
};

// =============================================================================
// TRANSITION DEFAULTS
// =============================================================================

export const defaultTransition: Transition = {
  type: 'fade',
  duration: 500,
  ease: 'power2.inOut',
};

// =============================================================================
// SCENE DEFAULTS
// =============================================================================

// New scenes are created by the Service from the one Shared default
// (`Shared/src/utils/sceneContentDefaults.ts`, spec 004); the editor has no
// second copy of it.

// =============================================================================
// PLAYBACK SETTINGS DEFAULTS
// =============================================================================

// The one default lives in Shared (spec 007: bgm 0.5); this is an alias, not a copy.
export const defaultPlaybackSettings: PlaybackSettings = DEFAULT_PLAYBACK_SETTINGS;

// =============================================================================
// ANCHOR POSITION HELPERS
// =============================================================================

/**
 * Get CSS positioning values from anchor and coordinates
 */
export function getAnchorCSS(position: Position): React.CSSProperties {
  const { anchor, x, y, unit } = position;
  const style: React.CSSProperties = {
    position: 'absolute',
  };

  // Horizontal positioning
  if (anchor.includes('left')) {
    style.left = `${x}${unit}`;
  } else if (anchor.includes('right')) {
    style.right = `${x}${unit}`;
  } else {
    // center
    style.left = '50%';
    style.transform = `translateX(calc(-50% + ${x}${unit}))`;
  }

  // Vertical positioning
  if (anchor.includes('top')) {
    style.top = `${y}${unit}`;
  } else if (anchor.includes('bottom')) {
    style.bottom = `${y}${unit}`;
  } else {
    // center
    style.top = '50%';
    const existingTransform = style.transform || '';
    style.transform = existingTransform
      ? existingTransform.replace(')', ` translateY(calc(-50% + ${y}${unit})))`)
      : `translateY(calc(-50% + ${y}${unit}))`;
  }

  return style;
}

/**
 * Get transform origin from anchor
 */
export function getTransformOrigin(anchor: Position['anchor']): string {
  const map: Record<Position['anchor'], string> = {
    'top-left': 'top left',
    'top-center': 'top center',
    'top-right': 'top right',
    'center-left': 'center left',
    'center': 'center center',
    'center-right': 'center right',
    'bottom-left': 'bottom left',
    'bottom-center': 'bottom center',
    'bottom-right': 'bottom right',
  };
  return map[anchor];
}

// =============================================================================
// ID GENERATION
// =============================================================================

export function generateElementId(type: 'sprite' | 'dialog' | 'sound' | 'signal' | 'scene' | 'choice'): string {
  return `${type}_${uuid().split('-')[0]}`;
}
